import * as React from 'react';
import { useNavigate } from 'react-router-dom';

/** True when the user is typing, so a shortcut should not steal the keystroke. */
function isEditingContext(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

interface Options {
  onFocusSearch: () => void;
  onShowHelp: () => void;
}

/**
 * Gmail-style shortcuts: a leading `g` then a destination key. Power users get
 * navigation without the mouse; everyone else never notices, because the
 * bindings are inert while typing and none of them use a lone modifier.
 */
export function useKeyboardShortcuts({ onFocusSearch, onShowHelp }: Options): void {
  const navigate = useNavigate();
  const pendingGoTo = React.useRef(false);
  const pendingTimer = React.useRef<number | null>(null);

  /*
   * The callbacks are held in a ref rather than listed as effect dependencies.
   *
   * Callers pass inline arrow functions, so they are new objects on every
   * render — with them in the dependency array the effect tore down and
   * re-subscribed constantly, and its cleanup calls `clearPending()`. A `g`
   * followed by any re-render (a query settling, a route resolving) lost the
   * chord, so `g` then `p` silently did nothing. Intermittent, and exactly the
   * kind of thing users report as "the shortcuts don't always work".
   */
  const callbacks = React.useRef({ onFocusSearch, onShowHelp });
  callbacks.current = { onFocusSearch, onShowHelp };

  React.useEffect(() => {
    const DESTINATIONS: Record<string, string> = {
      d: '/',
      l: '/leads',
      c: '/contacts',
      a: '/accounts',
      o: '/deals',
      t: '/tasks',
      u: '/settings/users',
      p: '/settings/profile',
    };

    const clearPending = () => {
      pendingGoTo.current = false;
      if (pendingTimer.current !== null) {
        window.clearTimeout(pendingTimer.current);
        pendingTimer.current = null;
      }
    };

    const handler = (event: KeyboardEvent) => {
      // Cmd/Ctrl+K is checked before the editing guard and before the
      // modifier guard: it is the chord everybody reaches for, and it should
      // work from inside a form field too.
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        callbacks.current.onFocusSearch();
        return;
      }

      if (isEditingContext(event.target)) return;
      // Leave the remaining browser and OS chords alone.
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      if (pendingGoTo.current) {
        const destination = DESTINATIONS[event.key.toLowerCase()];
        clearPending();
        if (destination) {
          event.preventDefault();
          navigate(destination);
        }
        return;
      }

      switch (event.key) {
        case 'g':
          pendingGoTo.current = true;
          // Abandon a half-typed chord rather than leaving it armed forever.
          pendingTimer.current = window.setTimeout(clearPending, 1500);
          break;
        case '/':
          event.preventDefault();
          callbacks.current.onFocusSearch();
          break;
        case '?':
          event.preventDefault();
          callbacks.current.onShowHelp();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
      clearPending();
    };
    // `navigate` is stable across renders, so this subscribes once.
  }, [navigate]);
}

export const SHORTCUTS: { keys: string; description: string }[] = [
  { keys: '/ or ⌘K', description: 'Search everything' },
  { keys: 'g then d', description: 'Go to dashboard' },
  { keys: 'g then l', description: 'Go to leads' },
  { keys: 'g then c', description: 'Go to contacts' },
  { keys: 'g then a', description: 'Go to accounts' },
  { keys: 'g then o', description: 'Go to deals (opportunities)' },
  { keys: 'g then t', description: 'Go to tasks' },
  { keys: 'g then u', description: 'Go to users' },
  { keys: 'g then p', description: 'Go to my profile' },
  { keys: '/', description: 'Focus search' },
  { keys: '?', description: 'Show this help' },
];
